import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { GoalPeriodStateChangeDialogComponent } from 'src/app/dialogs';
import { GoalInformationDialogComponent } from 'src/app/dialogs/goal-information-dialog/goal-information-dialog.component';
import { GoalInformation } from 'src/app/enums/goal-information.enum';
import { GoalPanelType } from 'src/app/enums/goal-panel-type.enum';
import { GoalPeriodStatus } from 'src/app/enums/goal-period-status.enum';
import { GoalTypeEnum } from 'src/app/enums/goal-type.enum';
import { ReviewPeriodStatusEnum } from 'src/app/enums/review-period-status.enum';
import { ReviewTypeEnum } from 'src/app/enums/review-type.enum';
import { TopicType } from 'src/app/enums/topic-type.enum';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { GoalsHelper } from 'src/app/helpers/goals.helper';
import { ReviewPeriodHelper } from 'src/app/helpers/review-period.helper';
import { RouteHelper } from 'src/app/helpers/route.helper';
import { CommentConfig } from 'src/app/models/comment-config.model';
import { Comment } from 'src/app/models/comment.model';
import { GoalPeriodApproval } from 'src/app/models/domain/goal-period-approvement.model';
import { Employee } from 'src/app/models/employee.model';
import { IResultModel, ResultModel } from 'src/app/models/general/result.model';
import { GoalPeriodCommentOptions } from 'src/app/models/goal-period-comment-options.model';
import { Goal, GoalModel } from 'src/app/models/goal.model';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { GoalsModel } from 'src/app/models/goals.model';
import { GoalTextModel } from 'src/app/models/goaltext.model';
import { GroupGoalsData } from 'src/app/models/group-goals-data.model';
import { KpiModel } from 'src/app/models/kpi.model';
import { ReviewPeriodGoalsForEmployee } from 'src/app/models/review-period-goals-for-employee.model';
import { ReviewPeriod } from 'src/app/models/review-period.model';
import { State } from 'src/app/models/state.model';
import { GoalService, KpiService, ReviewPeriodService, StateService } from 'src/app/services';
import { CommentService } from 'src/app/services/comment.service';
import { GroupGoalService } from 'src/app/services/group-goal.service';
import { StepIndicatorItem } from 'src/app/shared/components';
import { GaugeSettings } from 'src/app/shared/models';
import { AppState } from 'src/app/store/app.state';
import { EmployeeHelper } from 'src/app/helpers/employee.helper';
import { ErrorMessageService } from 'src/app/shared/services';

@UntilDestroy()
@Component({
    selector: 'goals',
    templateUrl: './goals.component.html',
    styleUrls: ['./goals.component.scss']
})
export class GoalsComponent implements OnInit {    

    public data: GoalsModel;

    public state: State;

    public openPeriods: ReviewPeriodGoalsForEmployee[] = [];
    public closedPeriods: ReviewPeriodGoalsForEmployee[] = [];

    public loading = true;
    
    public employees: Employee[] = [];
    
    public isAddGoal = false;

    public indicatorItems: StepIndicatorItem[] = [];

    public gaugeConfig: GaugeSettings = {
        breakpoints: [
            { maxWidth: 600, size: 160 },
            { maxWidth: 750, size: 180 },
            { maxWidth: Infinity, size: 220 },
        ]
    };

    public allowedStatusList: GoalPeriodStatus[] = [];
      
    public isMinExpectationNotMet: boolean = false;

    //selected in the dropdown
    public currentEmployeeId: number | null = null;    
    public openAccordion = null;

    public goalTypes = GoalTypeEnum;
    public goalPanelTypes = GoalPanelType;
    public goalPeriodStatus = GoalPeriodStatus;
    public EmployeeHelper = EmployeeHelper;

    public get hasPeriods(): boolean {
        return this.openPeriods.length > 0 || this.closedPeriods.length > 0;
    }

    public get isPeriodOpen(): boolean {
        return this.data.goalPeriod.status != GoalPeriodStatus.Approved;
    }

    public get hasOpenRp(): boolean {
        let items = this.openPeriods && this.openPeriods.filter(x => x.reviewPeriod.status === ReviewPeriodStatusEnum.Open);
        return items.length > 0;
    }

    public get hideCommentSection(): boolean {
        return this.data.isSelf && this.data?.goalPeriod?.comments.length === 0; 
    }

    public get hasRpInDateInterval(): boolean {
        let items = this.openPeriods && this.openPeriods.filter(x => !this.isOutOfDateInterval(x.reviewPeriod));
        return items.length > 0;
    }

    // a selected periodban a kommentek szerkeszthetőek-e az aktu7ális felhasználó számára, ha nem akkor csak view only
    public get isCommentsEditable(): boolean {
        return this.isPeriodOpen && !this.data.isSelf;
    }

    public get performanceBasedAmount(): number {
        if (!this.data.goalPeriod?.result || !this.data.goalPeriod.baseAmount) return 0;

        return Math.round(this.data.goalPeriod.baseAmount * this.data.goalPeriod.result);
    }

    public get goalsGaugeValue(): number | null {
        if (this.data.goalPeriod.result === undefined || this.data.goalPeriod.result === null || this.data?.goalPeriod?.status < GoalPeriodStatus.LeaderAssessment) return null;
        
        return this.data.goalPeriod.result * 100;
    }

    public get showGroupGoals(): boolean {
        // ha az adott személynek nincsenek beosztottjai akkor nem jelenik meg
        if (!this.data?.hasSubordinates) return false;
        return this.data?.goalPeriod.reviewPeriod.reviewPeriodGoalSettings?.isGroupGoalsEnabled;
    }

    public get isSelf(): boolean {
        return this.state?.me.value?.employee?.id === this.data?.goalPeriod?.employeeId;
    }

    public get leaderApprovals(): GoalPeriodApproval[] {
        return this.data?.goalPeriod?.approvals.filter(x => x.isLeader).sort((a, b) => a.employeeName.localeCompare(b.employeeName)) ?? [];
    }

    public get isSelfAssessment(): boolean {
        return this.data?.goalPeriod?.status === GoalPeriodStatus.SelfAssessment;
    }

    public get isLeaderAssessment(): boolean {
        return this.data?.goalPeriod?.status === GoalPeriodStatus.LeaderAssessment;
    }

    public get targetEmployeeApproval(): GoalPeriodApproval | undefined {
        let targetEmployeeId = this.data?.goalPeriod?.employeeId;
        let item = this.data?.goalPeriod?.approvals.find(x => x.employeeId === targetEmployeeId);
        return item;
    }

    // goal lists

    // public get textBasedGoals(): Goal[] {
    //     if (!this.data || !this.data.goalPeriod) return [];

    //     return this.data.goalPeriod.goals.filter(x => x.goalType == GoalTypeEnum.TextBasedGoal);
    // }

    // public get numericGoals(): Goal[] {
    //     if (!this.data || !this.data.goalPeriod) return [];

    //     return this.data.goalPeriod.goals.filter(x => x.goalType == GoalTypeEnum.NumericGoal);
    // }

    // public get groupGoals(): Goal[] {
    //     if (!this.data || !this.data.goalPeriod) return [];

    //     return this.data.goalPeriod.goals.filter(x => x.goalType == GoalTypeEnum.GroupGoal);
    // }

    public getGoals(goalType: GoalTypeEnum): Goal[] {
        if (!this.data || !this.data.goalPeriod) return [];

        return this.data.goalPeriod.textBasedGoals.filter(x => x.goalType === goalType);
    }

    //--
   
    public reviewTypes = ReviewTypeEnum;
    public isRpOpen = ReviewPeriodHelper.isRpOpen;
    public isRpNew = ReviewPeriodHelper.isRpNew;

    constructor(
        private goalService: GoalService,
        private commentService: CommentService,
        private groupGoalService: GroupGoalService,
        private reviewPeriodService: ReviewPeriodService,
        stateService: StateService,
        private kpiService: KpiService,
        private route: ActivatedRoute,
        private router: Router,
        private msgService: ErrorMessageService,
        private appState: AppState,
        private dialog: MatDialog,
        private cd: ChangeDetectorRef,
        private translate: TranslateService
        )
    {
        this.state = stateService.state;
    }


    ngOnInit() {
        this.route.params
        .pipe(untilDestroyed(this))
        .subscribe(params => {
            
            const reviewPeriodId = params['reviewPeriodId'] ? parseInt(params['reviewPeriodId']) : null;
            const employeeId = params['employeeId'] ? parseInt(params['employeeId']) : null;

            this.loadData(reviewPeriodId, employeeId);
        });        
    }

    loadData(reviewPeriodId: number, employeeId: number) {
        this.goalService.getGoals(reviewPeriodId, employeeId)
        .pipe(
            map(data => {
                // data.reviewPeriodGoalsForEmployee.map(item => {                                                                                                
                //     item.goalPeriod.goals.map(goal => {
                //         goal.goalText = goal.goalText || { name: '', goalType: GoalTypeEnum.Goal, isGlobal: false } as GoalTextModel;
                //         return goal;
                //     });

                //     item.commentConfig = this.createCommentConfig(item.goalPeriod);

                //     return item;
                // });

                return data;
            }),
            untilDestroyed(this)
        )
        .subscribe({ next: (data: GoalsModel) => { 

            this.data = data;

            this.indicatorItems = this.goalService.getGoalStatusList().map(x => {
                return {
                    id: x.id,
                    text: x.name,
                } as StepIndicatorItem;
            });
            // this.openPeriods = data.reviewPeriodGoalsForEmployee.filter(x => x.reviewPeriod.status != ReviewPeriodStatusEnum.Closed);
            // this.closedPeriods = data.reviewPeriodGoalsForEmployee.filter(x => x.reviewPeriod.status == ReviewPeriodStatusEnum.Closed);

            // this.selectedPeriod = data.reviewPeriodGoalsForEmployee.length > 0 ? data.reviewPeriodGoalsForEmployee[0] : null;
            // //this.currentPeriod.questionSets = this.createQuestionGroups();

            // console.log(this.selectedPeriod);
            
            // this.employees = data.employees;

            // if (employeeId)
            //     this.currentEmployeeId = Number(employeeId);
            // else if (data.employees.length > 0)
            //     this.currentEmployeeId = data.employees[0].id;
            // else
            //     this.currentEmployeeId = null;
                        
            this.setAllowedStatusList(this.data?.goalPeriod?.status);

            this.isMinExpectationNotMet = this.getIsMinExpectationNotMet();

            this.loading = false;
            
        }, error: (e) => {
            if (e.status === 403) {                        
                this.router.navigate(['/goals']);
            }
            this.loading = false;
        }});
    }

    // todo: kialakítani a logikát ami meghatározza, hogy mely státuszok engedélyezettek az aktuális goal státusz és bejelentkezett felhasználó alapján
    setAllowedStatusList(currentStatus: GoalPeriodStatus) {
        let list: GoalPeriodStatus[] = [];

        if (this.isSelf) //önértékelés, nem tud semerre státuszt váltani a dropdownban
        {
            if (!this.data?.hasBoss) // ha ő a cégvezető és nincs főnöke
            {
                if (currentStatus == GoalPeriodStatus.Editing)
                    list = [];
                else if (currentStatus == GoalPeriodStatus.SelfAssessment)
                    list = [GoalPeriodStatus.Editing];
                else if (currentStatus == GoalPeriodStatus.Finished)
                {
                    list = [GoalPeriodStatus.Editing, GoalPeriodStatus.SelfAssessment];
                    if (this.data?.hasBoss)
                        list.push(GoalPeriodStatus.LeaderAssessment);
                }
            }
            else
            {
                list = [];  // can not change status backward
            }
        }
        else if (this.data?.goalPeriod?.isCurrentUserBossOfTargetEmployee) //vezetői értékelés, csak lefelé tud státuszt váltani a dropdownban
        {
            if (currentStatus == GoalPeriodStatus.Editing)
                list = [];
            else if (currentStatus == GoalPeriodStatus.SelfAssessment)
                list = [GoalPeriodStatus.Editing];
            else if (currentStatus == GoalPeriodStatus.LeaderAssessment)
                list = [GoalPeriodStatus.Editing, GoalPeriodStatus.SelfAssessment];
            else if (currentStatus == GoalPeriodStatus.Finished)
                list = [GoalPeriodStatus.Editing, GoalPeriodStatus.SelfAssessment, GoalPeriodStatus.LeaderAssessment];
        }
        else if (this.state.isAssistant || this.state.isAdmin || this.state.isSuperAdmin) // HR Admin és admin, bármilyen irányban tud váltani státuszt (ha nem önértékelést csinál, azaz nem a saját értékelését írja)
        {
            list = [GoalPeriodStatus.Editing, GoalPeriodStatus.SelfAssessment];
            
            if (this.data?.hasBoss)
                list.push(GoalPeriodStatus.LeaderAssessment);

            list.push(GoalPeriodStatus.Finished);
            list.push(GoalPeriodStatus.Approved);
        }

        // remove current status from the list
        list = list.filter(x => x !== currentStatus);

        this.allowedStatusList = list;
    }

    public get isCompleted(): boolean {
        return this.data?.goalPeriod?.status === GoalPeriodStatus.Approved;
    }

    public getStatusText(status: GoalPeriodStatus): string {
        return this.goalService.getGoalStatusText(status);
    }

    showPerformanceBasedAmount(): boolean {
        return this.data?.goalPeriod?.status >= GoalPeriodStatus.LeaderAssessment;
    }

    showTotalPercent(): boolean {
        return  this.goalsGaugeValue !== null && this.data?.goalPeriod?.status >= GoalPeriodStatus.LeaderAssessment;
    }

    onGoalStatusSelect(newStatus: GoalPeriodStatus) {
        let currentStatus = this.data.goalPeriod.status;
        
        if (newStatus === currentStatus) return;

        const isStateDialogNeeded = this.isStateDialogNeed(currentStatus, newStatus);

        if (isStateDialogNeeded)
        {
            const dialogData = { fromState: currentStatus, toState: newStatus };

            const dialogRef = this.dialog.open(GoalPeriodStateChangeDialogComponent, {
                width: "770px",

                data: dialogData,
            });
            
            dialogRef.afterClosed().subscribe( (dialogResult: boolean) => {
                if (dialogResult)
                {         
                    this.changeState(newStatus);
                }
            });
        }
        else if (this.data.goalPeriod.status === GoalPeriodStatus.Approved && (this.state?.isAdmin || this.state?.isSuperAdmin)) {
            if (confirm(this.translate.instant('GOALS.CONFIRM_STATUS_CHANGE')))
            {
                this.changeState(newStatus);
            }
        }
        else
        {
            this.changeState(newStatus);
        }
    }

    reloadAfterChange(reviewPeriodId: number, employeeId: number, oldState: GoalPeriodStatus) {
        // SelfAssessment -> LeaderAssessment
        if (this.isSelf && oldState === GoalPeriodStatus.SelfAssessment && this.data.goalPeriod.status === GoalPeriodStatus.LeaderAssessment) {
            this.router.navigate(['/']);
        }
        else
            this.loadData(reviewPeriodId, employeeId);
    }

    changeState(newStatus: GoalPeriodStatus) {
        const reviewPeriodId = this.data.goalPeriod.reviewPeriod.id;
        const employeeId = this.data.goalPeriod.employeeId;
        const oldState = this.data.goalPeriod.status;

        this.goalService.changeGoalPeriodState(this.data.goalPeriod.id, newStatus).subscribe({next: () => { 
            this.data.goalPeriod.status = newStatus;
            this.msgService.showSnackBarMessage('changeStatusSuccess');
            //change was successful
            this.reloadAfterChange(reviewPeriodId, employeeId, oldState);

        }, error: (err: IResultModel<ResultModel>) => {
            this.msgService.handleError<ResultModel>(err);
        }});
    }
    
    isOutOfDateInterval(rp: ReviewPeriod): boolean {
        let dateFrom = new Date(rp.goalsStartDate.toString());
        let dateTo = new Date(rp.goalsEndDate.toString());
        let today = new Date();

        return today < dateFrom || today > dateTo;
    }

    isApproveChangeEnabled(item: GoalPeriodApproval) : boolean {
        if (!item) return false;
        return GoalPermissionsHelper.canApprovePeriod(this.data.goalPeriod, this.state, item.employeeId);
    }

    isOnlyOneApproveRemaining(): boolean {
        const allCount = this.data.goalPeriod.approvals.length;
        const approvedCount = this.data.goalPeriod.approvals.filter(x => x.id > 0).length;
        return allCount === approvedCount + 1;
    }

    isStateDialogNeed(currentStatus: GoalPeriodStatus, newStatus: GoalPeriodStatus): boolean {
        return (this.isSelf && 
                    (
                        (currentStatus === GoalPeriodStatus.Editing && newStatus === GoalPeriodStatus.SelfAssessment)
                        ||
                        (currentStatus === GoalPeriodStatus.SelfAssessment && newStatus === GoalPeriodStatus.LeaderAssessment)
                    )
                )
                || // Vezetői értékelés -> Befejezett állapot
                (!this.isSelf && (currentStatus === GoalPeriodStatus.LeaderAssessment && newStatus === GoalPeriodStatus.Finished))
                ||
                (currentStatus > newStatus)   // visszaváltás kísérlete
                ;
    }

    approveChanges(item: GoalPeriodApproval, reviewPeriodId: number, employeeId: number, oldState: GoalPeriodStatus) {
       
        // új jóváhagyás
        this.goalService.approveGoalPeriod(item).subscribe({
            next: (result) => {
                item.id = result.data.approval.id;

                if (this.data.goalPeriod.status !== result.data.goalPeriodStatus)
                    this.data.goalPeriod.status = result.data.goalPeriodStatus;

                this.reloadAfterChange(reviewPeriodId, employeeId, oldState);

                this.msgService.showSnackBarMessage('goalPeriodApproved');
            },
            error: (err) => {
                item.isApproved = false;
                this.msgService.handleError(err);
            }
        });
    }
    
    onChangeApproval(item: GoalPeriodApproval) { 
        const reviewPeriodId = this.data.goalPeriod.reviewPeriod.id;
        const employeeId = this.data.goalPeriod.employeeId;
        const oldState = this.data.goalPeriod.status;

        let currentStatus = this.data.goalPeriod.status;

        const isOnlyOneApproveRemaining = this.isOnlyOneApproveRemaining();

        if (item.id === 0) {
            
            const newStatus = this.data.goalPeriod.nextPossibleStatus; 
            if (isOnlyOneApproveRemaining && newStatus != GoalPeriodStatus.Approved) // we also need to change the state to the next one.
            {
                const dialogData = { fromState: currentStatus, toState: newStatus };

                const dialogRef = this.dialog.open(GoalPeriodStateChangeDialogComponent, {
                    width: "770px",

                    data: dialogData,
                });
                
                dialogRef.afterClosed().subscribe( (dialogResult: boolean) => {
                    if (dialogResult)
                    {         
                        this.approveChanges(item, reviewPeriodId, employeeId, oldState);
                    }
                    else
                    {
                        this.cd.detectChanges();
                        item.isApproved = false;
                    }
                });
            }
            else
            {
                 if (confirm(this.translate.instant('GOALS.CONFIRM_APPROVE'))) {
                    this.approveChanges(item, reviewPeriodId, employeeId, oldState);
                }
                else
                {
                    this.cd.detectChanges();
                    item.isApproved = false;
                }
            }
        }
        else // jóváhagyás visszavonása
        {
            this.goalService.deleteGoalPeriodApproval(item.id).subscribe({
                next: () => {
                    item.id = 0;
                    item.isApproved = false;
                    item.createdOn = new Date();

                    if (this.data.goalPeriod.status !== GoalPeriodStatus.Finished)
                        this.data.goalPeriod.status = GoalPeriodStatus.Finished;

                    this.loadData(reviewPeriodId, employeeId);

                    this.msgService.showSnackBarMessage('goalPeriodApprovalRevoked');
                },
                error: (err) => {
                    this.msgService.handleError(err);
                }
            });
        }
    }

    public isKpiMinExpectationNotMet(): boolean {
        const maxValue = this.data?.goalPeriod.reviewPeriod?.reviewPeriodGoalSettings?.numericGoalMaxResult;
        for(let numericGoal of this.data?.goalPeriod.numericGoals ?? [])
        {
            for(let kpi of numericGoal.kpiList)
            {
                if(kpi.minExpectation === null || kpi.achieved === null) continue;
                
                const ratio = GoalsHelper.calculateKPIRatio(kpi.planned, kpi.achieved, kpi.calculationType, maxValue);

                if(ratio < kpi.minExpectation)
                    return true;
            }
        }

        return false;
    }

    public getIsMinExpectationNotMet(): boolean {
        let mininumNotMetInNumericGoals = this.isKpiMinExpectationNotMet();
        
        // the same in text Based goals -> actions
        let mininumNotMetInTextBasedGoals = this.data?.goalPeriod.textBasedGoals.some(x => x.actions.some(y => y.minExpectation !== null && y.progress.some(z => z.progress !== null && y.minExpectation > z.progress))) ?? false;
    
        return  mininumNotMetInNumericGoals || mininumNotMetInTextBasedGoals;
    }

    createCommentConfig(goalPeriod: GoalPeriod): CommentConfig {
        return {
            employeeId: this.state?.me.value?.employee?.id ?? 0,
            topicId: TopicType.GoalPeriod,
            topicRef: goalPeriod.id,
            author: this.state.me.value.employee?.name
        } as CommentConfig;
    }

    getGroupGoalsData(goalPeriod: GoalPeriod): GroupGoalsData {
       
        let dt = new GroupGoalsData();
        dt.goalPeriodId = goalPeriod.id;
        dt.isSelf = this.data.isSelf;
        dt.loading = this.loading;
                 
        return dt;
    }

    // // calculates the font size based on the length of the value to fit within the maxWidth
    // public getFontSize(value: string, maxWidth: number, maxSize: number, minSize: number): string {
    //   let size = 28;
    //   let length = value.toString().length;
      
    //   if (length > 0) {
    //     size = Math.max(Math.min(maxWidth / length, maxSize), minSize);
    //   }
    //   if (size < 12) size = 12; // minimum font size
    //   if (size > maxSize) size = maxSize; // maximum font size

      
    //   return size + 'px';
    // }
   
    changeEmployee(employeeId) {
        this.router.navigate(['/goals', employeeId]);
    };
   
    addGoal(goalPeriod: GoalPeriod) {
        let found = goalPeriod.textBasedGoals.find(x => x.id == 0);

        if (!found)
            goalPeriod.textBasedGoals.push(new GoalModel());
    }

    addKpi(rpForEmployee: ReviewPeriodGoalsForEmployee) {
        let found = rpForEmployee.kpiList.find(x => x.id == 0);

        if (!found) {
            let model = new KpiModel();            
            model.aboutEmployeeId = this.currentEmployeeId;
            model.reviewPeriodId = rpForEmployee.reviewPeriod.id;
            
            rpForEmployee.kpiList.push(model);
        }
    }

    goalAdded(goal: Goal, goalPeriod: GoalPeriod) {
        if (!goalPeriod.id)
        {
            goalPeriod.id = goal.goalPeriodId;
        }
    }

    getQuarter(quarter: number): string {
        return GoalsHelper.getQuarter(quarter);
    }

    reloadComments(goalPeriod: GoalPeriod) {
        this.goalService.getComments(goalPeriod.id).subscribe(result => {
            goalPeriod.comments = result;
        });
    }

    saveComment(comment: Comment, goalPeriod: GoalPeriod) {
        if (comment.message === undefined) return;

        this.loading = true;

        let action = comment.id ? this.commentService.updateComment(comment) : this.commentService.insertComment(comment);

        action.subscribe({ next: (result) => {
            comment.id = result.data.id;
            this.reloadComments(goalPeriod);
            this.loading = false;
        }, error: (err) => {
            if (confirm(this.translate.instant('GOALS.ERROR_SAVE'))) {
                this.saveComment(comment, goalPeriod);
            }
            console.log(err);
            this.loading = false;
        }});
    };

    deleteComment(comment: Comment, goalPeriod: GoalPeriod) {
        this.loading = true;
        this.commentService.deleteComment(comment.id).subscribe({next: (result) => {
            this.reloadComments(goalPeriod);
            this.loading = false;
        }, error: (err: IResultModel<ResultModel>) => {
            if (confirm(this.translate.instant('GOALS.ERROR_DELETE'))) {
                this.deleteComment(comment, goalPeriod);
            }
            console.log(err);
            this.loading = false;
        }});
    }

    // updateComment(goalPeriod: GoalPeriod, comment) {
    //     this.loading = true;
    //     this.goalService.updateComment(goalPeriod.id, goalPeriod.reviewPeriod.id, goalPeriod.employeeId, comment).subscribe((result) => {
    //         this.loading = false;
    //     }, (error) => {
    //         if (confirm('Hiba történt a mentés során, megpróbálja újra?')) {
    //             this.updateComment(goalPeriod, comment);
    //         }
    //         console.log(error);
    //         this.loading = false;
    //     });
    // };

    getReviewPeriodDisplayName(rp: ReviewPeriod): string {
        return this.reviewPeriodService.getDisplayText(rp);
    }

    allowSettings(): boolean {
       return GoalPermissionsHelper.canAccessSettings(this.data?.goalPeriod, this.state);
    }

    allowChangeStatus(): boolean {
        return this.allowedStatusList.length > 0 && GoalPermissionsHelper.allowChangeStatus(this.data?.goalPeriod);
    }

    goToSettings(): void {
        this.router.navigate(['/goal-basic-settings', this.data?.goalPeriod.id]);
    }
       
    getApproveButtonText(item: GoalPeriodApproval): string {
        if (!item) return '';

        if (item.employeeId === this.state?.me.value?.employee?.id) {
            return !item.isApproved ? this.translate.instant('GOALS.WAITING_FOR_ACCEPTANCE') : this.translate.instant('GOALS.ACCEPTED');
        }
        else {
            return !item.isApproved ? this.translate.instant('GOALS.WAITING_FOR_ACCEPTANCE') : this.translate.instant('GOALS.ACCEPTED');
        }
    }

    showInformation(): void {
        this.dialog.open(GoalInformationDialogComponent, {
              width: "850px",
              data: { goalInfoType: GoalInformation.Main }
        });
    }
}
